import { useEffect } from 'react';
import { toast } from 'sonner';

/**
 * SpunFunReturnListener
 *
 * Hooks the MINDESIGN side of the After Dark <-> Spun Fun loop:
 *   1. Listens for `postMessage({ type: 'spunfun:return' })` from a Spun Fun tab
 *      that closes itself or pings back when the user clicks "BACK TO MINDESIGN".
 *   2. Listens for the `?from=spunfun` URL flag (used as a fallback when the user
 *      clicks a hard <a href> back-link instead of `window.opener.close()`).
 *
 * On match: pop a neon-pink "Welcome back from After Dark" toast, scroll the
 * page to the After Dark Portal section, and quietly clean the URL.
 */
export const SpunFunReturnListener = () => {
  useEffect(() => {
    const welcome = (source) => {
      try { console.log('[SpunFun] Welcome triggered via', source); } catch (_) {}
      // Defer to next tick so the global Sonner <Toaster> is guaranteed mounted
      setTimeout(() => {
        try {
          toast.success("Welcome back from After Dark", {
            description: "The lights are still on at MINDESIGN. Pick up where you left off.",
            duration: 6000,
          });
        } catch (_) {
          /* never break the app over a toast */
        }
      }, 600);

      // Scroll back to the portal so the user has a clear re-entry point
      setTimeout(() => {
        const el = document.getElementById('afterdark');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);

      // Fire an analytics event if Umami is loaded (no-op otherwise)
      if (typeof window !== 'undefined' && window.umami?.track) {
        window.umami.track('afterdark_return', { source });
      }
    };

    // 1) URL flag fallback (?from=spunfun)
    const url = new URL(window.location.href);
    if (url.searchParams.get('from') === 'spunfun') {
      welcome('url_flag');
      url.searchParams.delete('from');
      window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
    }

    // 2) postMessage from a Spun Fun tab
    const onMessage = (event) => {
      const data = event?.data;
      if (data && typeof data === 'object' && data.type === 'spunfun:return') {
        welcome('postmessage');
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return null;
};

export default SpunFunReturnListener;
